import { GUI } from 'lil-gui';
import AudioManager from '../audio/AudioManager'; // Import AudioManager type if needed
import { SubtitleManager } from '../subtitles/subtitleManager.js'; // Import SubtitleManager

/**
 * Manages the dat.gui interface for controlling visual parameters and playback.
 * It takes initial parameters, callbacks, and the AudioManager instance.
 */
export default class GuiManager {
    /**
     * Creates a GuiManager instance and sets up the GUI controls.
     * @param {object} params - An object containing the initial values for the visual GUI controls.
     * @param {object} callbacks - An object containing callback functions for visual controls and input triggers.
     * @param {AudioManager} audioManager - The application's AudioManager instance.
     * @param {SubtitleManager} subtitleManager - The application's SubtitleManager instance.
     * @param {function(object): void} callbacks.onColorChange
     * @param {function(object): void} callbacks.onBloomChange
     * @param {function(string): void} callbacks.onEffectChange
     * @param {function(): void} callbacks.onMicInputRequest
     * @param {function(FileList): void} callbacks.onMediaUploadRequest
     */
    constructor(params, callbacks, audioManager, subtitleManager) {
        /** @type {GUI} The lil-gui instance */
        this.gui = new GUI();
        /** @type {object} Local copy or reference to the visual parameters */
        this.params = params;
        /** @type {object} Callbacks to invoke on parameter changes */
        this.callbacks = callbacks;
        /** @type {AudioManager} Reference to the AudioManager instance */
        this.audioManager = audioManager; // Store AudioManager instance
        /** @type {SubtitleManager | null} Reference to the SubtitleManager instance */
        this.subtitleManager = subtitleManager;
        /** @type {object} Parameters specifically for playback control */
        this.playbackParams = {
            volume: 0.5,        // Initial volume matches AudioManager default
            currentTime: 0,   // Current playback time (updated by main.js)
            playPause: () => { // Function for the Play/Pause button
                if (this.audioManager) {
                    if (this.audioManager.isPlaying()) {
                        this.audioManager.pause();
                        // Ideally, update button text/icon here, but lil-gui makes this hard.
                        // We might rely on console logs or visualizer state for now.
                        console.log("GUI: Pause requested");
                    } else {
                        this.audioManager.resume(); // Use resume to handle paused state correctly
                        console.log("GUI: Resume/Play requested");
                    }
                }
            }
        };
        /** @type {import('lil-gui').Controller | null} Controller for the seek bar */
        this.seekBarController = null;
        /** @type {NodeJS.Timeout | null} Timer for pre-roll */
        this.preRollTimer = null;

        /** @type {object} Parameters specifically for subtitle timing */
        this.subtitleParams = {
            advance: () => { // Function for the Advance button
                if (this.subtitleManager) {
                    this.subtitleManager.adjustOffset(-0.1); // Advance by 0.1s
                }
            },
            delay: () => { // Function for the Delay button
                if (this.subtitleManager) {
                    this.subtitleManager.adjustOffset(0.1); // Delay by 0.1s
                }
            }
        };

        this._setupColorControls();
        this._setupBloomControls();
        this._setupEffectControls();
        this._setupMediaUploadControl();
        this._setupMicInputControl();
        this._setupPlaybackControls(); // Add playback controls
        this._setupSubtitleTimingControls(); // Add subtitle timing controls

        // Create hidden file input for media (audio and subtitles)
        /** @type {HTMLInputElement | null} Hidden file input for media */
        this.hiddenMediaInput = document.createElement('input');
        this.hiddenMediaInput.type = 'file';
        this.hiddenMediaInput.accept = 'audio/*,.srt'; // Accept audio and .srt files
        this.hiddenMediaInput.multiple = true; // Allow selecting multiple files
        this.hiddenMediaInput.style.display = 'none';
        this.hiddenMediaInput.addEventListener('change', (event) => {
            const files = event.target.files;
            if (files && files.length > 0 && this.callbacks.onMediaUploadRequest) {
                this.callbacks.onMediaUploadRequest(files); // Pass the FileList object
            }
            // Reset input value to allow uploading the same file(s) again
            event.target.value = null;
        });
        document.body.appendChild(this.hiddenMediaInput); // Add to DOM
    }

    /**
     * Sets up the dat.gui controls for color parameters.
     * @private
     */
    _setupColorControls() {
        const colorsFolder = this.gui.addFolder('Colors');
        // Link GUI control for 'red' to params.red
        colorsFolder.add(this.params, 'red', 0, 1).name('Red').onChange((value) => {
            // When the slider changes, call the registered callback
            if (this.callbacks.onColorChange) {
                this.callbacks.onColorChange({ u_red: Number(value) }); // Pass updated value
            }
        });
         // Link GUI control for 'green' to params.green
        colorsFolder.add(this.params, 'green', 0, 1).name('Green').onChange((value) => {
            if (this.callbacks.onColorChange) {
                this.callbacks.onColorChange({ u_green: Number(value) });
            }
        });
         // Link GUI control for 'blue' to params.blue
        colorsFolder.add(this.params, 'blue', 0, 1).name('Blue').onChange((value) => {
            if (this.callbacks.onColorChange) {
                this.callbacks.onColorChange({ u_blue: Number(value) });
            }
        });
        // colorsFolder.open(); // Optional: Keep the folder open by default
    }

    /**
     * Sets up the dat.gui controls for bloom effect parameters.
     * @private
     */
    _setupBloomControls() {
        const bloomFolder = this.gui.addFolder('Bloom');
         // Link GUI control for 'threshold' to params.threshold
        bloomFolder.add(this.params, 'threshold', 0, 1).name('Threshold').onChange((value) => {
            if (this.callbacks.onBloomChange) {
                this.callbacks.onBloomChange({ threshold: Number(value) });
            }
        });
         // Link GUI control for 'strength' to params.strength
        bloomFolder.add(this.params, 'strength', 0, 1).name('Strength').onChange((value) => {
            if (this.callbacks.onBloomChange) {
                this.callbacks.onBloomChange({ strength: Number(value) });
            }
        });
         // Link GUI control for 'radius' to params.radius
        bloomFolder.add(this.params, 'radius', 0, 1).name('Radius').onChange((value) => {
            if (this.callbacks.onBloomChange) {
                this.callbacks.onBloomChange({ radius: Number(value) });
            }
        });
        // bloomFolder.open(); // Optional: Keep the folder open by default
    }

    /**
     * Sets up the dat.gui control for selecting the visual effect.
     * @private
     */
    _setupEffectControls() {
        // Add control directly to the main GUI, not in a folder
        this.gui.add(this.params, 'visualEffect', ['icosahedron', 'particles'])
            .name('Visual Effect')
            .onChange((value) => {
                 if (this.callbacks.onEffectChange) {
                     this.callbacks.onEffectChange(value); // Pass the selected effect name string
                 }
            });
    }

    /**
     * Sets up the dat.gui control for triggering microphone input.
     * @private
     */
    _setupMicInputControl() {
        const micTrigger = {
            startMic: () => {
                if (this.callbacks.onMicInputRequest) {
                    console.log("Mic input button clicked, requesting mic input...");
                    this.callbacks.onMicInputRequest();
                }
            }
        };
        // Add the button to the main GUI
        this.gui.add(micTrigger, 'startMic').name('Use Microphone');
    }

    /**
     * Sets up the dat.gui control for triggering the combined media file upload.
     * @private
     */
    _setupMediaUploadControl() {
        const mediaUploadTrigger = {
            uploadMedia: () => {
                if (this.hiddenMediaInput) {
                     this.hiddenMediaInput.click(); // Trigger the hidden media input
                } else {
                    console.error("Hidden media input not found!");
                }
            }
        };
        // Add the button to the main GUI
        this.gui.add(mediaUploadTrigger, 'uploadMedia').name('Upload Media');
    }

    /**
     * Sets up the lil-gui controls for playback (Play/Pause, Seek, Volume).
     * @private
     */
    _setupPlaybackControls() {
        const playbackFolder = this.gui.addFolder('Playback');

        // Play/Pause Button
        playbackFolder.add(this.playbackParams, 'playPause').name('Play / Pause');

        // Seek Bar (Progress)
        // Initial duration is 0, will be updated later
        this.seekBarController = playbackFolder.add(this.playbackParams, 'currentTime', 0, 0.1)
            .name('Progress (s)')
            .step(0.1)
            .listen() // Update GUI when playbackParams.currentTime changes
            .onChange((value) => {
                if (this.audioManager) {
                    const seekValue = Number(value);
                    const isSeekingToStart = seekValue < 0.1; // Threshold for seeking to start

                    if (isSeekingToStart) {
                        // --- Seeking to Start: Initiate 3s Pre-roll --- 
                        console.log("GUI: Seek near 0 requested, initiating 3s pre-roll...");
                        
                        // 1. Visually snap the slider to 0 immediately
                        this.playbackParams.currentTime = 0;
                        // We might need to force update lil-gui display if listen() isn't fast enough
                        if(this.seekBarController) this.seekBarController.updateDisplay();
                        
                        // 2. Stop/Pause current playback and set next offset to 0
                        if (this.audioManager.isPlaying()) {
                            this.audioManager.sound.stop(); // Stop completely to ensure clean state
                        } else {
                            // If paused, ensure the internal pause marker isn't interfering
                             this.audioManager._pausedTime = 0; 
                        }
                        // Set the hardware offset for the *next* play call
                        this.audioManager.sound.offset = 0;
                        
                        // 3. Start 3-second timer
                        // Clear any existing timer to prevent overlaps if user scrubs quickly
                        if (this.preRollTimer) {
                            clearTimeout(this.preRollTimer);
                        }
                        this.preRollTimer = setTimeout(() => {
                            console.log("GUI: 3s pre-roll finished, seeking to 0 and playing.");
                            // 4. After delay, call seek(0) which will now force playback
                            this.audioManager.seek(0); 
                            this.preRollTimer = null; // Clear timer reference
                        }, 3000); // 3 seconds delay

                    } else {
                        // --- Normal Seek --- 
                        // Clear pre-roll timer if user seeks away from start during the delay
                        if (this.preRollTimer) {
                            clearTimeout(this.preRollTimer);
                            this.preRollTimer = null;
                            console.log("GUI: Pre-roll cancelled by seeking elsewhere.");
                        }
                        // Perform normal seek immediately
                        this.audioManager.seek(seekValue);
                        console.log(`GUI: Seek requested to ${seekValue}`);
                    }
                }
            });

        // Volume Slider
        playbackFolder.add(this.playbackParams, 'volume', 0, 1)
            .name('Volume')
            .onChange((value) => {
                if (this.audioManager) {
                    this.audioManager.setVolume(Number(value));
                }
            });

        playbackFolder.open(); // Keep playback controls open by default
    }

    /**
     * Sets up the GUI controls for adjusting subtitle timing.
     * @private
     */
    _setupSubtitleTimingControls() {
        if (!this.subtitleManager) {
            console.warn("SubtitleManager not provided to GuiManager, skipping timing controls.");
            return;
        }

        const timingFolder = this.gui.addFolder('Subtitle Timing');
        // Add buttons
        timingFolder.add(this.subtitleParams, 'advance').name('Advance (-0.1s)');
        timingFolder.add(this.subtitleParams, 'delay').name('Delay (+0.1s)');
        // Add offset display (read-only)
        // Using add() and disable() to show the value, listen() updates it
        // timingFolder.add(this.subtitleParams, 'offset')
        //     .name('Offset (s)')
        //     .min(-30) // Set reasonable min/max for display range
        //     .max(30)
        //     .step(0.1)
        //     .listen()
        //     .disable(); // Make it read-only

        timingFolder.close(); // Keep timing controls closed by default
    }

    /**
     * Updates the maximum value and current value of the seek bar.
     * @param {number} duration - The total duration of the audio in seconds.
     */
    updateSeekBarDuration(duration) {
        if (this.seekBarController) {
            const validDuration = Math.max(0.1, duration); // Ensure max > min
            this.seekBarController.max(validDuration);
            this.seekBarController.updateDisplay(); // Refresh the GUI element
            console.log(`GUI: Seek bar max duration updated to ${validDuration}`);
        }
    }

    /**
     * Updates the current value displayed by the seek bar.
     * Should be called frequently (e.g., in the animation loop).
     * @param {number} currentTime - The current playback time in seconds.
     */
    updateSeekBarValue(currentTime) {
        // Directly update the bound parameter. .listen() should update the GUI.
        if (this.playbackParams.currentTime !== currentTime) {
             this.playbackParams.currentTime = currentTime;
             // No need to call updateDisplay() here if .listen() is working
        }
    }

    // Optional: Method to hide/show GUI
    // toggleVisibility() { ... }
} 